/**
 * Appointment rules shared by both CI list views.
 *
 * Lives apart from either tab so the two cannot drift: "did this consult happen"
 * decides who leaves the pre-consultation queue AND who enters the
 * post-consultation one, and those two answers have to be the same answer.
 */

import { Appointment } from '@medplum/fhirtypes';

/**
 * Statuses that mean the appointment did not happen and never will.
 *
 * `noshow` is deliberately NOT here. A no-show is a real event with real
 * consequences — it keeps a lead in the pre-consultation queue and drives that
 * tab's chip — it just is not an *occurrence*. `appointmentHasOccurred` handles
 * it separately, and the stage bot's `isFulfilled()` in
 * `packages/bots/src/services/procedure-stage.ts` delegates here, so the
 * PAC lists and the procedure stage share one answer to "did this happen".
 */
const VOID_APPOINTMENT_STATUSES: Appointment['status'][] = ['cancelled', 'entered-in-error'];

/**
 * Every field optional: these rules run over GraphQL selections, which return
 * only what was asked for, and over Healthie-synced appointments that routinely
 * carry no terminal status. `Pick` would keep `status` required and force
 * callers to invent one.
 */
export type ListAppointment = Partial<Pick<Appointment, 'start' | 'end' | 'status'>>;

export function isVoidAppointment(appointment: ListAppointment): boolean {
  return Boolean(appointment.status && VOID_APPOINTMENT_STATUSES.includes(appointment.status));
}

/**
 * Did this appointment actually take place?
 *
 * Shared with the stage bot: `isFulfilled()` in
 * `packages/bots/src/services/procedure-stage.ts` delegates here (BAR-2588), so
 * the PAC queues and the procedure stage give the same answer.
 *
 * Falls back to the clock when status is unset: Healthie-synced appointments
 * frequently carry no terminal status even long after the fact.
 */
export function appointmentHasOccurred(appointment: ListAppointment, now: Date): boolean {
  if (isVoidAppointment(appointment) || appointment.status === 'noshow') {
    return false;
  }

  if (appointment.status === 'fulfilled') {
    return true;
  }

  const ended = appointment.end ?? appointment.start;
  return ended !== undefined && new Date(ended).getTime() < now.getTime();
}

/**
 * Is this appointment still ahead of the patient?
 *
 * The complement of `appointmentHasOccurred` over the same clock: a live
 * booking (not void, not no-show, not fulfilled) whose end — or start, when no
 * end was recorded — is still in the future. A call in progress counts.
 */
export function appointmentIsUpcoming(appointment: ListAppointment, now: Date): boolean {
  if (isVoidAppointment(appointment) || appointment.status === 'noshow' || appointment.status === 'fulfilled') {
    return false;
  }

  const ends = appointment.end ?? appointment.start;
  return ends !== undefined && new Date(ends).getTime() > now.getTime();
}
